<template>
    <div class="flex flex-col h-full">
        <page-header :buttons="pageActions.buttons" :additional="pageActions.additional" @method="methodHandler">
            <template slot="title">{{ item.full_name }}<small class="text-sm ml-2">{{ item.member_id_display ? item.member_id_display : ''}}</small></template>
        </page-header>

        <tabs :tabs="tabs" :active="startTab" @setTab="setActiveTab" ref="tabs"></tabs>

        <div class="flex xl:flex-row lg:flex-col md:flex-col sm:flex-col flex-grow">
            <div class="px-4 sm:px-6 lg:px-6 bg-white border-r border-gray-400 bg-gray-200" :class="activeTab === 'applications' ? 'xl:w-full lg:w-full md:w-full sm:w-full' : 'xl:w-3/4 lg:w-full md:w-full sm:w-full'">
                <div class="mt-2 grid grid-cols-12 row-gap-5 col-gap-4 sm:grid-cols-12" v-if="!loading" v-show="activeTab === 'details'">
                    <div class="sm:col-span-12">
                        <table class="w-full internal-table">
                            <thead>
                                <tr>
                                    <th colspan="2">Contact Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="w-40">Name</td>
                                    <td>{{ item.first + ' ' + item.last }}</td>
                                </tr>
                                <tr v-if="item.other_first">
                                    <td class="w-40">Other Name <small v-if="item.other_name_preferred">(Preferred)</small></td>
                                    <td>{{ item.other_first+' '+item.last }}</td>
                                </tr>
                                <tr>
                                    <td>Email</td>
                                    <td>{{ item.email }}</td>
                                </tr>
                                <tr>
                                    <td>Mobile</td>
                                    <td>{{ item.mobile }}</td>
                                </tr>
                                <tr>
                                    <td>Date of Birth</td>
                                    <td>{{ item.date_of_birth }}</td>
                                </tr>
                                <tr>
                                    <td>Address</td>
                                    <td>{{ item.address }} {{ item.suburb }} {{ item.state }} {{ item.postcode }}</td>
                                </tr>
                                <tr v-if="item.postal_address">
                                    <td>Postal Address</td>
                                    <td>{{ item.postal_address }} {{ item.postal_suburb }} {{ item.postal_state }} {{ item.postal_postcode }}</td>
                                </tr>
                                <tr v-if="item.parent">
                                    <td>Associated With</td>
                                    <td><router-link :to="'/members/' + item.parent.id + '/view'" class="text-default-500">{{ item.parent.first + ' ' + item.parent.last }}</router-link></td>
                                </tr>
                                <tr v-if="item.important_information">
                                    <td>Important Information</td>
                                    <td v-html="item.important_information.replace(/(?:\r\n|\r|\n)/g, '<br>')"></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="sm:col-span-12">
                        <table class="w-full internal-table">
                            <thead>
                                <tr>
                                    <th colspan="2">Custom Fields</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(field, index) in item.custom_fields" :key="index">
                                    <td class="w-1/4">{{ field.label }}</td>
                                    <td>{{ field.answer_display }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="sm:col-span-12" v-if="item.identification_files && item.identification_files.length > 0">
                        <table class="w-full -mt-4 mb-4 internal-table">
                            <thead>
                                <tr>
                                    <th>Identification Files</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(file, index) in item.identification_files" :key="index">
                                    <td style="font-weight: normal;"><a :href="s3URL + '/' + file.path " target="_blank" class="text-gray-800 hover:text-default-500 truncate">{{ file.filename }}</a></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="sm:col-span-12" v-if="item.cards && item.cards.length > 0">
                        <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900 mb-3">Cards</h2>
                        <div v-for="(file, index) in item.cards" :key="index">
                            <img :src="s3URL + '/' + file.path " alt="" />
                        </div>
                    </div>
                </div>
                <div class="mt-1 grid grid-cols-1 row-gap-2 col-gap-4 sm:grid-cols-12" v-if="!loading" v-show="activeTab === 'applications'">
                    <div class="flex justify-start mt-2 flex-wrap col-span-12">
                        <div class="relative pr-4 w-1/4">
                            <label class="block w-full text-xs pb-1 text-charcoal-900">
                                Application Form
                            </label>
                            <input-select
                                id="form"
                                :options="options.forms"
                                @input="updateFormFilter"
                                placeholder="Select Application Form"/>
                        </div>
                        <div class="relative pr-4 w-1/4">
                            <label class="block w-full text-xs pb-1 text-charcoal-900">
                                Application Status
                            </label>
                            <input-select
                                id="status"
                                v-model="selectedStatus"
                                :options="options.statuses"
                                placeholder="Select Status"/>
                        </div>
                        <div class="w-1/2 flex" v-if="selectedForm">
                            <div class="flex-grow text-center">
                                <label class="block w-full text-xs pb-1 text-charcoal-900">
                                    Limit
                                </label>
                                <div class="mt-2">
                                    <span class="text-lg text-default-500" v-if="calculating">
                                        <svg aria-hidden="true" focusable="false" data-prefix="fad" data-icon="spinner-third" class="svg-inline--fa fa-spinner-third fa-w-16 fa-spin" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><g class="fa-group"><path class="fa-secondary" fill="currentColor" d="M478.71 364.58zm-22 6.11l-27.83-15.9a15.92 15.92 0 0 1-6.94-19.2A184 184 0 1 1 256 72c5.89 0 11.71.29 17.46.83-.74-.07-1.48-.15-2.23-.21-8.49-.69-15.23-7.31-15.23-15.83v-32a16 16 0 0 1 15.34-16C266.24 8.46 261.18 8 256 8 119 8 8 119 8 256s111 248 248 248c98 0 182.42-56.95 222.71-139.42-4.13 7.86-14.23 10.55-22 6.11z" opacity="0.4"></path><path class="fa-primary" fill="currentColor" d="M271.23 72.62c-8.49-.69-15.23-7.31-15.23-15.83V24.73c0-9.11 7.67-16.78 16.77-16.17C401.92 17.18 504 124.67 504 256a246 246 0 0 1-25 108.24c-4 8.17-14.37 11-22.26 6.45l-27.84-15.9c-7.41-4.23-9.83-13.35-6.2-21.07A182.53 182.53 0 0 0 440 256c0-96.49-74.27-175.63-168.77-183.38z"></path></g></svg>
                                    </span>
                                    <span v-else>${{ amounts.limit.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
                                </div>
                            </div>
                            <div class="flex-grow text-center">
                                <label class="block w-full text-xs pb-1 text-charcoal-900">
                                    Used
                                </label>
                                <div class="mt-2">
                                    <span class="text-lg text-default-500" v-if="calculating">
                                        <svg aria-hidden="true" focusable="false" data-prefix="fad" data-icon="spinner-third" class="svg-inline--fa fa-spinner-third fa-w-16 fa-spin" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><g class="fa-group"><path class="fa-secondary" fill="currentColor" d="M478.71 364.58zm-22 6.11l-27.83-15.9a15.92 15.92 0 0 1-6.94-19.2A184 184 0 1 1 256 72c5.89 0 11.71.29 17.46.83-.74-.07-1.48-.15-2.23-.21-8.49-.69-15.23-7.31-15.23-15.83v-32a16 16 0 0 1 15.34-16C266.24 8.46 261.18 8 256 8 119 8 8 119 8 256s111 248 248 248c98 0 182.42-56.95 222.71-139.42-4.13 7.86-14.23 10.55-22 6.11z" opacity="0.4"></path><path class="fa-primary" fill="currentColor" d="M271.23 72.62c-8.49-.69-15.23-7.31-15.23-15.83V24.73c0-9.11 7.67-16.78 16.77-16.17C401.92 17.18 504 124.67 504 256a246 246 0 0 1-25 108.24c-4 8.17-14.37 11-22.26 6.45l-27.84-15.9c-7.41-4.23-9.83-13.35-6.2-21.07A182.53 182.53 0 0 0 440 256c0-96.49-74.27-175.63-168.77-183.38z"></path></g></svg>
                                    </span>
                                    <span v-else>${{ amounts.used.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
                                </div>
                            </div>
                            <div class="flex-grow text-center">
                                <label class="block w-full text-xs pb-1 text-charcoal-900">
                                    Remaining
                                </label>
                                <div class="mt-2">
                                    <span class="text-lg text-default-500" v-if="calculating">
                                        <svg aria-hidden="true" focusable="false" data-prefix="fad" data-icon="spinner-third" class="svg-inline--fa fa-spinner-third fa-w-16 fa-spin" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><g class="fa-group"><path class="fa-secondary" fill="currentColor" d="M478.71 364.58zm-22 6.11l-27.83-15.9a15.92 15.92 0 0 1-6.94-19.2A184 184 0 1 1 256 72c5.89 0 11.71.29 17.46.83-.74-.07-1.48-.15-2.23-.21-8.49-.69-15.23-7.31-15.23-15.83v-32a16 16 0 0 1 15.34-16C266.24 8.46 261.18 8 256 8 119 8 8 119 8 256s111 248 248 248c98 0 182.42-56.95 222.71-139.42-4.13 7.86-14.23 10.55-22 6.11z" opacity="0.4"></path><path class="fa-primary" fill="currentColor" d="M271.23 72.62c-8.49-.69-15.23-7.31-15.23-15.83V24.73c0-9.11 7.67-16.78 16.77-16.17C401.92 17.18 504 124.67 504 256a246 246 0 0 1-25 108.24c-4 8.17-14.37 11-22.26 6.45l-27.84-15.9c-7.41-4.23-9.83-13.35-6.2-21.07A182.53 182.53 0 0 0 440 256c0-96.49-74.27-175.63-168.77-183.38z"></path></g></svg>
                                    </span>
                                    <span v-else>${{ amounts.remaining.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
                                </div>
                            </div>
                            <div class="flex-grow text-center">
                                <label class="block w-full text-xs pb-1 text-charcoal-900">
                                    Pending
                                </label>
                                <div class="mt-2">
                                    <span class="text-lg text-default-500" v-if="calculating">
                                        <svg aria-hidden="true" focusable="false" data-prefix="fad" data-icon="spinner-third" class="svg-inline--fa fa-spinner-third fa-w-16 fa-spin" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><g class="fa-group"><path class="fa-secondary" fill="currentColor" d="M478.71 364.58zm-22 6.11l-27.83-15.9a15.92 15.92 0 0 1-6.94-19.2A184 184 0 1 1 256 72c5.89 0 11.71.29 17.46.83-.74-.07-1.48-.15-2.23-.21-8.49-.69-15.23-7.31-15.23-15.83v-32a16 16 0 0 1 15.34-16C266.24 8.46 261.18 8 256 8 119 8 8 119 8 256s111 248 248 248c98 0 182.42-56.95 222.71-139.42-4.13 7.86-14.23 10.55-22 6.11z" opacity="0.4"></path><path class="fa-primary" fill="currentColor" d="M271.23 72.62c-8.49-.69-15.23-7.31-15.23-15.83V24.73c0-9.11 7.67-16.78 16.77-16.17C401.92 17.18 504 124.67 504 256a246 246 0 0 1-25 108.24c-4 8.17-14.37 11-22.26 6.45l-27.84-15.9c-7.41-4.23-9.83-13.35-6.2-21.07A182.53 182.53 0 0 0 440 256c0-96.49-74.27-175.63-168.77-183.38z"></path></g></svg>
                                    </span>
                                    <span v-else>${{ amounts.pending.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <pro-table
                        :static-data="filteredSubmissions.sort((a, b) => a[sortSubmissions.column] < b[sortSubmissions.column] ? sortSubmissions.direction === 'desc' ? 1 : -1 : sortSubmissions.direction === 'desc' ? -1 : 1)"
                        resource="submissions"
                        prefix="/"
                        :fields="fields.submissions"
                        :limit="100"
                        ref="datatable"
                        class="px-0 sm:col-span-12"
                        @action="actionHandler"
                        table-class="text-xs"
                        sort="first"
                        direction="ASC"
                        delete-label="full_name"
                        :pagination="false"
                        :redirect="false"
                        :actions="actions.submissions"
                        @sort="handleSubmissionSort"
                    ></pro-table>
                    <SmsModal :to="sendTo" v-if="showSMSModal" model="ApplicationSubmission" :id="rowID" @close="showSMSModal = false" @sent="showSMSModal = false" :meta="SMSMeta" />
                </div>
                <div class="mt-1 grid grid-cols-1 row-gap-2 col-gap-4 sm:grid-cols-12" v-if="!loading" v-show="activeTab === 'associates'">
                    <pro-table
                        :static-data="item.relationships"
                        resource="members"
                        prefix="/"
                        :fields="fields.relationships"
                        :limit="100"
                        ref="datatable"
                        class="px-0 sm:col-span-12"
                        @action="actionHandler"
                        table-class="text-xs"
                        sort="first"
                        direction="ASC"
                        delete-label="full_name"
                        :pagination="false"
                        :redirect="false"
                        :actions="actions.relationships"
                    ></pro-table>
                </div>
                <div class="mt-1 grid grid-cols-1 row-gap-2 col-gap-4 sm:grid-cols-12" v-if="!loading" v-show="activeTab === 'logs'">

                </div>
                <tasks v-if="!loading"
                    v-show="activeTab === 'tasks'"
                    :id="item.id"
                    :tasks="item.tasks"
                    model="Member" />
                <notes v-if="!loading"
                    v-show="activeTab === 'notes'"
                    :id="item.id"
                    :notes="item.notes"
                    model="Member" />
            </div>
            <div class="xl:w-1/4 lg:w-full md:w-full sm:w-full px-4 sm:px-6 lg:px-6" v-if="activeTab !== 'applications'">
                <div class="mt-2 grid grid-cols-1 row-gap-2 col-gap-4 sm:grid-cols-12" v-if="!loading">
                    <div class="sm:col-span-12 mt-3" v-if="supportCode">
                        <div class="w-full flex flex-row justify-center items-center h-12 border border-green-900 bg-green-300 text-green-900">
                            <span class="pr-2 font-bold">Support Code:</span>
                            <span>{{ supportCode }}</span>
                        </div>
                    </div>
                    <div class="sm:col-span-12">
                        <table class="w-full mb-6 internal-table">
                            <thead>
                                <tr>
                                    <th colspan="2">Meta</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="item.meta.xero">
                                    <td>Xero Record</td>
                                    <td>
                                        <a :href="`https://go.xero.com/Contacts/View/${item.meta.xero}`" target="_blank" class="text-default-500">View</a>
                                    </td>
                                </tr>
                                <tr>
                                    <td>Last Updated</td>
                                    <td>{{ new Date(item.meta.updated_at) | moment("DD/MM/YYYY h:mm a") }}</td>
                                </tr>
                                <tr>
                                    <td>Created At</td>
                                    <td>{{ new Date(item.meta.created_at) | moment("DD/MM/YYYY h:mm a") }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import * as router from "vue-router";
    import Tabs from "../../mixins/Tabs";
    import axios from "axios";
    import Notes from "../../components/Notes";
    import SmsModal from '../../components/modals/SmsModal'
    import Tasks from '../../components/Tasks'
    export default {
        name: "MemberView",
        props: {
            id: { required: true },
            return: { required: false, default: '/members' },
            page: { required: false, default: 'members' }
        },
        components: {
            Tasks,
            Notes,
            SmsModal
        },
        mixins: [
            Tabs
        ],
        data: function () {
            return {
                selectedForm: null,
                selectedStatus: null,
                startTab: null,
                loading: true,
                item: {},
                pageActions: {},
                fields: {
                    relationships: [
                        {
                            name: 'Name',
                            column: 'full_name',
                            route: '/members/{id}/view',
                            linked: true,
                            sortCol: 'last'
                        },
                        {
                            name: 'DOB',
                            column: 'date_of_birth',
                        },
                        {
                            name: 'Member ID',
                            column: 'member_id_display',
                        },
                        {
                            name: 'Type',
                            column: 'type.name',
                        },
                        {
                            name: 'Approved',
                            column: 'applications.approved',
                            align: 'center'
                        },
                        {
                            name: 'Pending',
                            column: 'applications.pending',
                            align: 'center'
                        },
                        {
                            name: 'Incomplete',
                            column: 'applications.incomplete',
                            align: 'center'
                        },
                        {
                            name: 'Completed',
                            column: 'applications.completed',
                            align: 'center'
                        },
                    ],
                    submissions: [
                        {
                            name: 'ID',
                            column: 'friendly_id_display',
                            highlight: 'highlight'
                        },
                        {
                            name: 'Form',
                            column: 'application_form.name'
                        },
                        {
                            name: 'Supplier',
                            column: 'supplier.name'
                        },
                        {
                            name: 'Amount',
                            column: 'amount',
                            currency: true
                        },
                        // {
                        //     name: 'Remaining',
                        //     column: 'values.remaining',
                        //     currency: true
                        // },
                        {
                            name: 'Created',
                            column: 'created_at',
                            date: true,
                            columnClass: 'whitespace-no-wrap',
                            format: 'DD/MM/YYYY',
                            width: 'w-24',
                            sortCol: 'created_at'
                        },
                        {
                            name: 'Application',
                            column: 'application_status.name',
                            highlight: 'application_status.colour'
                        },
                        {
                            name: 'FAS Status',
                            column: 'fas_status.name',
                            highlight: 'fas_status.colour'
                        },
                        {
                            name: 'Supplier Status',
                            column: 'supplier_status.name',
                            highlight: 'supplier_status.colour'
                        }
                    ]
                },
                actions: {
                    relationships: {
                        display: true,
                        class: [],
                        options: [
                            { label: 'View', link: 'view', icon: 'eye', iconClass: 'text-gray-400',  type: 'router-link', tooltip: 'View', permissions: 'members.show' },
                            { label: 'Edit', link: 'edit', icon: 'edit', iconClass: 'text-gray-400', type: 'router-link', tooltip: 'Edit', permissions: 'members.edit' },
                        ]
                    },
                    submissions: {
                        display: true,
                        class: [],
                        options: [
                            { label: 'SMS', method: 'sendSMS', icon: 'paper-plane',  type: 'method', tooltip: 'Send SMS', permissions: 'submissions.show' },
                            { label: 'View', link: 'view', icon: 'eye', iconClass: 'text-gray-400',  type: 'router-link', tooltip: 'View', permissions: 'submissions.show' },
                            { label: 'Edit', link: 'edit', icon: 'edit', iconClass: 'text-gray-400', type: 'router-link', tooltip: 'Edit', permissions: 'submissions.edit' },
                        ]
                    }
                },
                options: {
                    forms: [],
                    statuses: []
                },
                amounts: null,
                calculating: true,
                supportCode: null,
                s3URL: process.env.VUE_APP_S3_URL,
                showSMSModal: false,
                rowID: null,
                SMSMeta: null,
                sortSubmissions: {
                    column: 'created_at',
                    direction: 'desc'
                }
            }
        },
        methods: {
            impersonate(){
                window.open(process.env.VUE_APP_PWA_URL + '/impersonate?code=' +this.item.id)
            },
            updateFormFilter(payload){
                this.selectedForm = null;
                setTimeout(() => {
                    this.selectedForm = payload;
                    this.getRemainingLimit();
                }, 10);
            },
            sendSupportCode: function(){
                this.$store.commit('TOGGLE_BACKDROP', {open: true});
                axios.get(process.env.VUE_APP_API_URL + '/members/' + this.item.id + '/support-code/').then(response => {
                    if (response.data.success){
                        this.getData();
                        this.supportCode = response.data.code;
                        this.$store.commit('TOGGLE_BACKDROP', {open: false});
                    }
                });
            },
            sendAppLink: function(){
                this.$store.commit('TOGGLE_BACKDROP', {open: true});
                axios.get(process.env.VUE_APP_API_URL + '/members/' + this.item.id + '/app-link/').then(response => {
                    if (response.data.success){
                        this.getData();
                        this.$store.commit('TOGGLE_BACKDROP', {open: false});
                    }
                });
            },
            sendSMS(payload){
                this.sendTo = {
                    user_id: this.item.id,
                    name: this.item.first + ' ' + this.item.last,
                    number: this.item.mobile ? this.item.mobile : this.item.parent ? this.item.parent.mobile : null
                }
                this.rowID = payload.item.id

                this.SMSMeta = {
                    user_id: this.item.id,
                    first: this.item.first,
                    last: this.item.last,
                    submissionID: payload.item.friendly_id_display,
                    status: payload.item.application_status.name,
                    amount: '$'+payload.item.amount.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","),
                    supplier: payload.item.supplier ? payload.item.supplier.name : null,
                }
                this.showSMSModal = true;
            },
            getRemainingLimit: function(){
                this.amounts = null;

                if (this.selectedForm){
                    this.calculating = true;

                    axios.get(process.env.VUE_APP_API_URL + '/members/' + this.item.id + '/application-forms/' + this.selectedForm + '/limits').then(response => {
                        this.calculating = false;
                        this.amounts = response.data.data;
                    });
                }
            },
            getData: function(){
                this.$Progress.start();

                axios.get(process.env.VUE_APP_API_URL + '/members/'+this.id)
                .then(response => {
                    this.item = response.data;

                    this.pageActions = {
                        buttons: [
                            {
                                label: 'Send App Link',
                                icon: 'sms',
                                type: 'method',
                                method: 'sendAppLink',
                                iconClass: 'text-blue-700',
                                display: this.$store.getters.permissions.indexOf('members.edit') !== -1
                            },
                            {
                                label: 'Send Support Code',
                                icon: 'sms',
                                type: 'method',
                                method: 'sendSupportCode',
                                iconClass: 'text-green-700',
                                display: this.$store.getters.permissions.indexOf('members.edit') !== -1
                            },
                            {
                                label: 'Edit',
                                icon: 'edit',
                                type: 'router-link',
                                link: '/members/'+ this.id + '/edit',
                                iconClass: 'text-gray-700',
                                display: !!(this.$store.getters.permissions.indexOf('members.edit') !== -1 || this.$store.getters.nimdarepus)
                            },
                            {
                                label: 'Close',
                                icon: 'times',
                                type: 'router-link',
                                iconClass: 'text-red-500',
                                link: this.return ? this.return : '/members',
                            },
                        ],
                        additional: [
                            {
                                label: 'Impersonate',
                                icon: 'user',
                                type: 'method',
                                method: 'impersonate',
                                iconClass: 'text-black-700',
                                display: this.$store.getters.permissions.indexOf('members.edit') !== -1
                            },
                            {
                                label: 'Delete',
                                icon: 'trash-alt',
                                type: 'method',
                                method: 'deleteModal',
                                iconClass: 'text-red-500',
                                display: !!(this.$store.getters.permissions.indexOf('members.destroy') !== -1 || this.$store.getters.nimdarepus)
                            }
                        ]
                    };

                    this.$Progress.finish();
                    this.loading = false;
                })
                .catch(() => {
                    this.$Progress.finish();
                    this.loading = false;
                });
            },
            methodHandler: function (method) {
                this[method]();
            },
            actionHandler: function (data) {
                this[data.method](data);
            },
            handleSubmissionSort: function (payload) {
                this.sortSubmissions = payload;
            }
        },
        mounted() {
            this.getData();
        },
        beforeMount() {
            if (this.$store.getters.forms.length === 0){
                this.$store.dispatch('GET_APPLICATION_FORMS').then(() => {
                    this.options.forms = this.$store.getters.forms.map(function (form) {
                        return { label: form.name, value: form.id };
                    });
                });
            } else {
                this.options.forms = this.$store.getters.forms.map(function (form) {
                    return { label: form.name, value: form.id };
                });
            }

            if (this.$store.getters.applicationStatuses.length === 0){
                this.$store.dispatch('GET_APPLICATION_STATUSES').then(() => {
                    this.options.statuses = this.$store.getters.applicationStatuses.map(function (type) {
                        return { label: type.name, value: type.id };
                    });
                });
            } else {
                this.options.statuses = this.$store.getters.applicationStatuses.map(function (type) {
                    return { label: type.name, value: type.id };
                });
            }
        },
        watch: {
            id: function(newVal, oldVal){
                this.loading = true;
                this.getData();
                this.$refs.tabs.setActiveTab('details');
                this.startTab = 'details';
                this.selectedForm = null;
            }
        },
        computed: {
            filteredSubmissions() {
                if (this.item.submissions) {
                    return this.item.submissions.filter(submission => {
                        if (this.selectedForm !== null && this.selectedStatus !== null && this.selectedForm !== '' && this.selectedStatus !== '') {
                            return this.selectedForm === submission.application_form_id && this.selectedStatus === submission.application_status_id;
                        } else if (this.selectedForm !== null && this.selectedForm !== '') {
                            return this.selectedForm === submission.application_form_id;
                        } else if (this.selectedStatus !== null && this.selectedStatus !== '') {
                            return this.selectedStatus === submission.application_status_id;
                        } else {
                            return true;
                        }
                    })
                } else {
                    return [];
                }
            },
            tabs() {
                return [
                    {
                        name: 'Details',
                        slug: 'details'
                    },
                    {
                        name: 'Applications' + ' (' + (this.filteredSubmissions ? this.filteredSubmissions.length : 0) + ')',
                        slug: 'applications'
                    },
                    {
                        name: 'Associates' + ' (' + (this.item.relationships ? this.item.relationships.length : 0) + ')',
                        slug: 'associates'
                    },
                    {
                        name: 'Tasks',
                        slug: 'tasks'
                    },
                    {
                        name: 'Notes',
                        slug: 'notes'
                    }
                ]
            }
        }
    }
</script>

<style scoped>

</style>
